/* eslint-disable eag/no-css-property */
import {Action, Actions, Choice, FormControl} from 'platform/components';
import {VStack, Text, Box, HStack, Space, Scroll, getSize} from 'platform/foundation';
import {css} from 'styled-components';

import {useFieldArray, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';
import {useUserAbsenceIndicators} from '@dms/shared';

import {buildArray, Nullish} from 'shared/web';

import type {KanbanSettings} from '../types/KanbanSettings';
import type {KanbanUser} from '../types/KanbanUser';
import {getKanbanUsersInBranches} from '../utils/getKanbanUsersInBranches';
import {getWatchedUserColor} from '../utils/getWatchedUserColor';
import {KanbanUserAvatar} from './KanbanUserAvatar';

// Design-system steps (Scroll converts to calc(step * Nrem), ~4px/step).
// 120 * 4px = 480px, ~10 user rows before scrolling (row 36px + 8px gap).
const SELECTED_USERS_MAX_HEIGHT = 120;

type UserControlProps = {
  control: FormControl<KanbanSettings>;
  currentUserId: string;
  users: KanbanUser[] | Nullish;
};

export function UserControl(props: UserControlProps) {
  const fieldArray = useFieldArray({
    control: props.control,
    name: 'selectedUsers',
    keyName: 'formFieldId',
  });
  const {getStatusIndicator} = useUserAbsenceIndicators();
  const watchedBranchIds = useWatch({control: props.control, name: 'branchIds'});

  const onUserSelect = (userId: string | null) => {
    const user = props.users?.find((user) => user.id === userId);
    if (!user) {
      return;
    }
    fieldArray.append({...user, isHidden: false});
  };

  const userOptions = getKanbanUsersInBranches(
    props.users ?? [],
    watchedBranchIds,
    props.currentUserId
  )
    .filter((user) => !fieldArray.fields.some((selectedUser) => selectedUser.id === user.id))
    .map((user) => ({
      label: `${user.fullName} <${user.email}>`,
      value: user.id,
      statusIndicator: getStatusIndicator(user.id),
    }));

  return (
    <VStack spacing={4}>
      <Choice
        value=""
        autoFocus
        options={userOptions}
        isSearchable
        isNotClearable
        placeholder={i18n.t('general.labels.searchPlaceholder')}
        onChange={onUserSelect}
      />
      <Scroll auto maxHeight={SELECTED_USERS_MAX_HEIGHT}>
        <VStack spacing={2}>
          {fieldArray.fields.map((field, fieldIndex) => (
            <div
              key={field.formFieldId}
              css={css`
                border-left: 2px solid ${getWatchedUserColor(fieldIndex)};
                padding: 0 ${getSize(2)};
              `}
            >
              <HStack align="center" minHeight={9}>
                <Box opacity={field.isHidden ? 0.2 : 1}>
                  <KanbanUserAvatar name={field.fullName} color={getWatchedUserColor(fieldIndex)} />
                </Box>
                <Space horizontal={2} />
                <Text size="small">{field.fullName}</Text>
                <Space fillAvailable />
                <Actions
                  size="small"
                  actions={buildArray<Action>()
                    .add({
                      type: 'iconButton',
                      isDisabled:
                        !field.isHidden &&
                        fieldArray.fields.filter((selectedUser) => !selectedUser.isHidden)
                          .length === 1,
                      icon: field.isHidden ? 'action/visibility_off' : 'action/visibility',
                      onClick: () => {
                        fieldArray.update(fieldIndex, {...field, isHidden: !field.isHidden});
                      },
                    })
                    .when(field.id !== props.currentUserId, {
                      type: 'iconButton',
                      icon: 'navigation/close',
                      onClick: () => {
                        fieldArray.remove(fieldIndex);
                      },
                    })}
                />
              </HStack>
            </div>
          ))}
        </VStack>
      </Scroll>
    </VStack>
  );
}
